"use client";

import React from 'react';
import Link from 'next/link';
import { useParams } from 'next/navigation';
import { useSavedCandidates } from '@/context/SavedCandidatesContext';

export interface CandidateProps {
  id: string | number;
  slug?: string;
  name: string;
  avatar: string;
  profession: string;
  skills: string[];
  languages?: string[];
  education?: string;
  salary?: string;
  location: string;
  isOnline?: boolean;
  isFeatured?: boolean;
}

export default function CandidateCard({ candidate }: { candidate: CandidateProps }) {
  const params = useParams();
  const language = (params?.lang as string) || 'en';
  
  const { id, slug, name, avatar, profession, skills, education, location, isOnline = true, isFeatured = false } = candidate;

  const rawSkills = Array.isArray(skills) ? skills : [];
  const normalizedSkills = rawSkills.map((s: any) => typeof s === 'string' ? s : (s?.name || s?.skill || s?.title || '')).filter(Boolean);

  // Render max 2 skills to keep on one line, plus a "+X" badge if there are more
  const displaySkills = normalizedSkills.slice(0, 2);
  const extraSkillsCount = normalizedSkills.length > 2 ? normalizedSkills.length - 2 : 0;
  
  const { isSaved, saveCandidate, removeCandidate } = useSavedCandidates();

  const [isCompanyUser, setIsCompanyUser] = React.useState(false);
  React.useEffect(() => {
    setIsCompanyUser(localStorage.getItem('userRole') === 'company');
  }, []);

  return (
    <div style={{
      background: 'white',
      borderRadius: '1rem',
      padding: '2rem 1.5rem',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)',
      border: '1px solid #f3f4f6',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      position: 'relative',
      transition: 'box-shadow 0.2s'
    }}>
      {/* Featured Banner */}
      {isSaved(id) && (
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          background: '#1c7bff', // theme blue
          color: 'white',
          padding: '0.25rem 0.75rem',
          borderTopLeftRadius: '1rem',
          borderBottomRightRadius: '1rem',
          fontSize: '0.75rem',
          fontWeight: 'bold',
          display: 'flex',
          alignItems: 'center',
          gap: '0.25rem'
        }}>
          ★
        </div>
      )}

      {/* Bookmark Icon */}
      {isCompanyUser && (
        <button 
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (isSaved(id)) {
              removeCandidate(id);
            } else {
              saveCandidate(id);
            }
          }}
          style={{
            position: 'absolute',
            top: '1.25rem',
            right: '1.25rem',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: isSaved(id) ? '#1c7bff' : '#9ca3af'
          }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill={isSaved(id) ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"></path>
          </svg>
        </button>
      )}

      {/* Avatar Container */}
      <Link href={`/${language}/user/${id}`} style={{ textDecoration: 'none', cursor: 'pointer' }}>
        <div style={{
          width: '80px',
          height: '80px',
          borderRadius: '50%',
          position: 'relative',
          marginBottom: '1rem',
          overflow: 'hidden',
          backgroundColor: '#f3f4f6'
        }}>
          <img 
            src={avatar || 'https://via.placeholder.com/150'} 
            alt={name} 
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            onError={(e) => {
              (e.target as HTMLElement).style.display = 'none';
            }}
          />
        </div>
      </Link>

      {/* Basic Info */}
      <Link href={`/${language}/user/${id}`} style={{ textDecoration: 'none', color: 'inherit' }}>
        <h3 style={{ margin: 0, fontSize: '1.125rem', fontWeight: 600, color: '#111827', textAlign: 'center', cursor: 'pointer' }}>
          {name}
        </h3>
      </Link>
      <p style={{ margin: '0.25rem 0 1.25rem', fontSize: '0.875rem', color: '#6b7280', textAlign: 'center' }}>
        {profession || 'Professional'}
      </p>

      {/* Skills Badges */}
      <div style={{ display: 'flex', flexWrap: 'nowrap', gap: '0.5rem', justifyContent: 'center', alignItems: 'center', marginBottom: '1.5rem', minHeight: '32px', overflow: 'hidden', width: '100%' }}>
        {displaySkills.map((skill, idx) => (
          <span key={idx} style={{
            background: '#f3f4f6',
            color: '#374151',
            padding: '0.25rem 0.75rem',
            borderRadius: '1rem',
            fontSize: '0.75rem',
            fontWeight: 500,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            maxWidth: '120px'
          }}>
            {skill}
          </span>
        ))}
        {extraSkillsCount > 0 && (
          <span style={{
            background: '#1c7bff',
            color: 'white',
            width: '24px',
            height: '24px',
            borderRadius: '50%',
            fontSize: '0.7rem',
            fontWeight: 600,
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            {extraSkillsCount}+
          </span>
        )}
      </div>

      {/* Education & Location */}
      <div style={{ display: 'flex', width: '100%', justifyContent: 'space-between', marginBottom: '1.5rem', gap: '0.5rem' }}>
        <div style={{ textAlign: 'center', flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginBottom: '0.25rem' }}>Education</div>
          <div style={{ fontSize: '0.875rem', fontWeight: 600, color: '#374151', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={education || 'Higher Education'}>
            {education || 'Higher Education'}
          </div>
        </div>
        <div style={{ textAlign: 'center', flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginBottom: '0.25rem' }}>Location</div>
          <div style={{ fontSize: '0.875rem', fontWeight: 600, color: '#374151', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={location || 'N/A'}>
            {location || 'N/A'}
          </div>
        </div>
      </div>

      {/* View Profile Button */}
      <Link href={`/${language}/user/${id}`} style={{ width: '100%', textDecoration: 'none' }}>
        <button style={{
          width: '100%',
          background: '#1c7bff',
          color: 'white',
          border: 'none',
          padding: '0.75rem 0',
          borderRadius: '2rem',
          fontWeight: 600,
          fontSize: '0.9rem',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          transition: 'background-color 0.2s'
        }}>
          View Profile
        </button>
      </Link>
    </div>
  );
}
